import type { UpdateProfileFormData, UserImagesFormData } from '@/core/data/auth/auth.interface';
import type { ClaimedHandshakeDto, HandshakeDto, LiveHandshakeDto } from '@/core/data/handshake/handshake.interface';
import type { StandInVerdict } from '@/core/data/handshake/handshake.type';
import { StorageKeyType } from '@/core/data/storage/storage.interface';
import type { StorageRepository } from '@/core/data/storage/storage.repository';
import type {
  BaseUserDto,
  BaseUserInfo,
  GetUsernameDto,
  GetUsersOptions,
  UserDto,
  ValidateUserDto,
} from '@/core/data/user/user.interface';
import { createLogger } from '@/core/helpers/logger.helper';
import { UserApi } from '@/core/network/api/user/user.api';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import { HandleApiError, HttpError } from '@/core/network/http/httpError';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { NotificationTopics } from '@/views/composables/constants/gam.constants';
import type { BaseClubDto } from '../club/club.interface';
import type { CityDto, GeoCoords, UserGeoData, UserLocationData } from '../location/location.interface';

import mockIpLocationResponse from './mockIpLocationResponse.json';

const logger = createLogger('UserRepository');

export class UserRepository {
  private readonly httpAPI: UserApi;
  private readonly session: StorageRepository;

  constructor(httpAPI: UserApi, session: StorageRepository) {
    this.httpAPI = httpAPI;
    this.session = session;
  }

  async getLocationFromCountry(): Promise<GamResponse<UserLocationData> | null> {
    try {
      const response = await this.httpAPI.getLocationFromCountry();
      if (response.data?.data) {
        const { latitude, longitude, city, country } = response.data.data;
        return {
          data: {
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            city: city,
            country: country,
          },
        };
      } else {
        return null;
      }
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getIpLocation(): Promise<GamResponse<UserLocationData> | null> {
    try {
      if (window.location.hostname === 'localhost') {
        return { data: mockIpLocationResponse };
      }

      const response = await this.httpAPI.getIpLocation();
      if (response.data) {
        const { latitude, longitude } = response.data;
        return {
          data: {
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            city: response.data.city,
            country: response.data.country,
          },
        };
      } else {
        return null;
      }
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateLocation(geoData: UserGeoData): Promise<GamResponse<UserGeoData> | null> {
    try {
      const response = await this.httpAPI.updateLocation(geoData);
      if (response.data?.data) {
        return { data: response.data.data };
      } else {
        return null;
      }
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateCity(coordinates: GeoCoords): Promise<GamResponse<CityDto> | null> {
    try {
      const response = await this.httpAPI.updateCity(coordinates);
      if (response.data?.data) {
        return { data: response.data.data };
      } else {
        return null;
      }
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getUsers(listId: GamListId, options?: GetUsersOptions): Promise<GamResponse<BaseUserDto[]> | null> {
    try {
      const response = await this.httpAPI.getUsers(listId, options);
      return response.data?.data ? { data: response.data.data, cursor: response.data.cursor } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getFollowersByQuery(query: string): Promise<GamResponse<BaseUserDto[]> | null> {
    try {
      const response = await this.httpAPI.getFollowersByQuery(query);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getFollowers(): Promise<GamResponse<BaseUserDto[]> | null> {
    try {
      const response = await this.httpAPI.getFollowers();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getFollowing(): Promise<GamResponse<BaseUserDto[]> | null> {
    try {
      const response = await this.httpAPI.getFollowing();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getRecentInvitees(): Promise<GamResponse<BaseUserDto[]> | null> {
    try {
      const response = await this.httpAPI.getRecentInvitees();
      if (Array.isArray(response.data?.data)) {
        return { data: response.data.data };
      }

      if (Array.isArray(response.data)) {
        return { data: response.data };
      }

      return { data: [] };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getUser(id: string): Promise<GamResponse<UserDto> | null> {
    try {
      const response = await this.httpAPI.getUser(id);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getUserByName(username: string): Promise<GamResponse<UserDto> | null> {
    try {
      const response = await this.httpAPI.getUserByName(username);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getUserInfo(refresh?: boolean): Promise<GamResponse<BaseUserInfo> | null> {
    try {
      const savedUser = this.session.get<BaseUserInfo>(StorageKeyType.UserInfo);
      if (savedUser && !refresh) return { data: savedUser };
      const response = await this.httpAPI.getUserInfo();
      if (response.data?.data) {
        this.session.set<BaseUserInfo>(StorageKeyType.UserInfo, response.data.data);
        return { data: response.data.data };
      }
      return null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getUserClubs(refresh?: boolean): Promise<GamResponse<BaseClubDto[]> | null> {
    try {
      const savedClubs = this.session.get<BaseClubDto[]>(StorageKeyType.UserClubs);
      if (savedClubs && !refresh) return { data: savedClubs };
      const response = await this.httpAPI.getUserClubs();
      if (response.data?.data) {
        return { data: response.data.data };
      }
      return null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateProfile(profile: UpdateProfileFormData): Promise<GamResponse<boolean>> {
    try {
      const response = await this.httpAPI.updateProfile(profile);
      if (response.data?.data) {
        this.session.set<BaseUserInfo>(StorageKeyType.UserInfo, response.data.data);
        return { data: true };
      }
      return { data: false };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateNotificationPreferences(
    notificationPreferences: import('@/core/data/user/user.interface').NotificationPreferences,
  ): Promise<GamResponse<import('@/core/data/user/user.interface').NotificationPreferences | undefined>> {
    try {
      const response = await this.httpAPI.updateNotificationPreferences(notificationPreferences);
      const body = response.data?.data;
      if (body !== undefined && body !== null) {
        return { data: body };
      }
      // API may return 200 without a body; treat the payload we sent as the updated prefs.
      return { data: notificationPreferences };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateUnitTypePreferences(unitType: string): Promise<GamResponse<string>> {
    try {
      const response = await this.httpAPI.updateUnitTypePreferences(unitType);
      return response.data?.data ? { data: response.data.data } : { data: '' };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async markActive(): Promise<GamResponse<boolean>> {
    try {
      await this.httpAPI.markActive();
      return { data: true };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateAccentHue(accentHue: number | null): Promise<GamResponse<any>> {
    try {
      const response = await this.httpAPI.updateAccentHue(accentHue);
      return response.data?.data ? { data: response.data.data } : { data: null };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateRecentClockControls(
    controls: { minutes: number; increment: number }[],
  ): Promise<GamResponse<{ minutes: number; increment: number }[]>> {
    const saved = this.session.get<BaseUserInfo>(StorageKeyType.UserInfo);
    if (saved) {
      this.session.set<BaseUserInfo>(StorageKeyType.UserInfo, { ...saved, recentClockControls: controls });
    }

    try {
      await this.httpAPI.updateRecentClockControls(controls);
      return { data: controls };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateEmoji(emoji: string): Promise<GamResponse<any>> {
    try {
      const response = await this.httpAPI.updateEmoji(emoji);
      return response.data?.data ? { data: response.data.data } : { data: null };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async updateImages(images: UserImagesFormData): Promise<GamResponse<boolean>> {
    try {
      const response = await this.httpAPI.updateImages(images);
      if (response.data?.data) {
        this.session.set<BaseUserInfo>(StorageKeyType.UserInfo, response.data.data);
        return { data: true };
      }
      return { data: false };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getWorldHandshakePairs(userId: string): Promise<GamResponse<{ worldUniqueHandshakePairs: number }> | null> {
    try {
      const response = await this.httpAPI.getWorldHandshakePairs(userId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getHandshakePartners(userId: string): Promise<GamResponse<{ handshakes: HandshakeDto[] }> | null> {
    try {
      const response = await this.httpAPI.getHandshakePartners(userId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async getUsername(username: string): Promise<GamResponse<GetUsernameDto> | null> {
    try {
      const response = await this.httpAPI.getUsername(username);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async validateUsername(username: string): Promise<GamResponse<ValidateUserDto> | null> {
    try {
      const response = await this.httpAPI.validateUsername(username);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async storeNotificationToken(token: string, topics: NotificationTopics[] = []): Promise<GamResponse<boolean>> {
    try {
      const response = await this.httpAPI.storeNotificationToken(token, topics);
      if (response.data?.data) {
        return { data: true };
      }
      return { data: false };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async deactivateNotificationToken(token: string): Promise<GamResponse<boolean>> {
    try {
      const response = await this.httpAPI.deactivateNotificationToken(token);
      if (response.data?.data) {
        return { data: true };
      }
      return { data: false };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async deactivateAllNotificationTokens(): Promise<GamResponse<boolean>> {
    try {
      const response = await this.httpAPI.deactivateAllNotificationTokens();
      if (response.data?.data) {
        return { data: true };
      }
      return { data: false };
    } catch (e) {
      return HandleApiError(e, logger);
    }
  }

  async toggleFollow(id: string, isFollowing?: boolean): Promise<boolean> {
    try {
      const response = await this.httpAPI.toggleFollow(id, isFollowing);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async toggleBlock(id: string, isBLocked?: boolean): Promise<boolean> {
    try {
      const response = await this.httpAPI.toggleBlock(id, isBLocked);
      return response.status >= 200 && response.status < 300;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async generateHandshakeCode(): Promise<GamResponse<{ code: string; expiresAt: string }> | null> {
    try {
      const response = await this.httpAPI.generateHandshakeCode();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async resolveHandshakeCode(
    code: string,
  ): Promise<GamResponse<{ userId: string; username: string; token: string }> | null> {
    try {
      const response = await this.httpAPI.resolveHandshakeCode(code);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async offerHandshake(
    playerId: string,
    image: File | null | string | undefined,
    options: {
      gambitId?: string;
      locationId?: string;
      token?: string;
      note?: string;
      suggestionSeed?: string;
      suggestionRound?: number;
    },
  ): Promise<(GamResponse<HandshakeDto> & { status: number }) | null> {
    try {
      const response = await this.httpAPI.offerHandshake(playerId, image, options);
      return response.data?.data ? { data: response.data.data, status: response.status } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async createPasserbyHandshake(
    options: { suggestionSeed?: string; suggestionRound?: number } = {},
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.createPasserbyHandshake(options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async claimPasserbyHandshake(code: string): Promise<(GamResponse<ClaimedHandshakeDto> & { status: number }) | null> {
    try {
      const response = await this.httpAPI.claimPasserbyHandshake(code);
      return response.data?.data ? { data: response.data.data, status: response.status } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async acceptHandshake(
    handshakeId: string,
    image: File | null | string | undefined,
    note?: string,
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.acceptHandshake(handshakeId, image, note);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getLiveHandshakes(): Promise<GamResponse<LiveHandshakeDto[]> | null> {
    try {
      const response = await this.httpAPI.getLiveHandshakes();
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async closeHandshake(handshakeId: string): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.closeHandshake(handshakeId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async deleteHandshake(handshakeId: string): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.deleteHandshake(handshakeId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async addHandshakeImage(
    handshakeId: string,
    image: File,
    signal?: AbortSignal,
    positionIndex?: number,
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.addHandshakeImage(handshakeId, image, signal, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async moveHandshakeImage(
    handshakeId: string,
    imageId: string,
    positionIndex: number | null,
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.moveHandshakeImage(handshakeId, imageId, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async removeHandshakeImage(handshakeId: string, imageId: string): Promise<GamResponse<void> | null> {
    try {
      const response = await this.httpAPI.removeHandshakeImage(handshakeId, imageId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async dealHandshakePosition(seed: string, round: number): Promise<GamResponse<{ positionIndex: number }> | null> {
    try {
      const response = await this.httpAPI.dealHandshakePosition(seed, round);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async getHandshakePositions(handshakeId: string): Promise<GamResponse<{
    positions: number[];
    openPositions?: number[];
    declinedPositions?: number[];
    suggestionPositions: number[] | null;
  }> | null> {
    try {
      const response = await this.httpAPI.getHandshakePositions(handshakeId);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async declineHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<GamResponse<{ declinedPositions: number[] }> | null> {
    try {
      const response = await this.httpAPI.declineHandshakePosition(handshakeId, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async startHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<GamResponse<{ startedPositions: number[] }> | null> {
    try {
      const response = await this.httpAPI.startHandshakePosition(handshakeId, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async checkHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<GamResponse<{ positions: number[] }> | null> {
    try {
      const response = await this.httpAPI.checkHandshakePosition(handshakeId, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async uncheckHandshakePosition(
    handshakeId: string,
    positionIndex: number,
  ): Promise<GamResponse<{ positions: number[] }> | null> {
    try {
      const response = await this.httpAPI.uncheckHandshakePosition(handshakeId, positionIndex);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async updateHandshakeSuggestion(
    handshakeId: string,
    change: { swapColours?: boolean; minutes?: number; increment?: number },
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.updateHandshakeSuggestion(handshakeId, change);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async nextHandshakeSuggestion(
    handshakeId: string,
    options: { keepColours?: boolean } = {},
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.nextHandshakeSuggestion(handshakeId, options);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async setHandshakeNote(handshakeId: string, note: string): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.setHandshakeNote(handshakeId, note);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }

  async setStandInVerdict(
    handshakeId: string,
    verdict: StandInVerdict,
    name?: string,
  ): Promise<GamResponse<HandshakeDto> | null> {
    try {
      const response = await this.httpAPI.setStandInVerdict(handshakeId, verdict, name);
      return response.data?.data ? { data: response.data.data } : null;
    } catch (e) {
      const httpError = e as HttpError;
      logger.error(`${httpError.error.code} => ${httpError.error.message}`);
      throw httpError.error;
    }
  }
}
